import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.tsx';
import { Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import type { MerchantDetails } from '../merchants/use-merchants.ts';
import { consentCapturedAt, localIsoDate } from './consent-date.ts';
import { EntryRow } from './SuppressionPage.tsx';
import {
  CONSENT_SOURCES,
  consentSourceLabel,
  type PersonConsentSource,
  useConsentQuery,
  useRecordConsent,
} from './use-consent.ts';
import { useMerchantSuppressionQuery, useSuppressionMutations } from './use-suppression.ts';

/**
 * The merchant profile's compliance card (M6): whether the merchant can be texted and why not, the
 * active suppression entries (admins release them), "mark do not contact" (anyone who sees the
 * merchant), and the consent records.
 */
export function MerchantCompliance({
  merchant,
  people,
}: {
  merchant: MerchantDetails;
  people: ReadonlyMap<string, string>;
}) {
  return (
    <div className="flex flex-col gap-5">
      <TextingStatus merchant={merchant} people={people} />
      <hr className="border-hairline" />
      <ConsentRecords merchantId={merchant.id} people={people} />
    </div>
  );
}

function TextingStatus({
  merchant: m,
  people,
}: {
  merchant: MerchantDetails;
  people: ReadonlyMap<string, string>;
}) {
  const { workspaceId, role } = useWorkspaceClaims();
  const admin = role === 'admin';
  const entries = useMerchantSuppressionQuery(workspaceId, m.id, m.phone, admin);
  const { add } = useSuppressionMutations(workspaceId);
  const [marking, setMarking] = useState(false);
  const [note, setNote] = useState('');

  function onMark(e: FormEvent) {
    e.preventDefault();
    add.mutate({ phone: m.phone, note }, { onSuccess: () => setMarking(false) });
  }

  return (
    <div className="flex flex-col gap-3" data-testid="texting-status">
      {m.optedOutAt ? (
        <Alert tone="error">
          Opted out on {new Date(m.optedOutAt).toLocaleString()}. No texts are sent. Only the
          merchant can resubscribe, by texting START.
        </Alert>
      ) : null}
      {m.doNotContact ? (
        <Alert tone="error">Do not contact: {m.dncReason ?? 'marked do not contact'}.</Alert>
      ) : null}
      {!m.optedOutAt && !m.doNotContact ? (
        <p className="callout text-ink-muted">Can be texted.</p>
      ) : null}

      {admin ? (
        entries.isPending ? (
          <Spinner label="Loading suppression entries" />
        ) : entries.isError ? (
          <Alert tone="error">{entries.error.message}</Alert>
        ) : entries.data.length > 0 ? (
          <ul className="divide-y divide-hairline" aria-label="Active suppression entries">
            {entries.data.map((entry) => (
              <EntryRow key={entry.id} entry={entry} names={people} />
            ))}
          </ul>
        ) : null
      ) : null}

      {!m.doNotContact ? (
        marking ? (
          <form className="flex flex-col gap-2" onSubmit={onMark} noValidate>
            <TextField
              label="Why? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="Asked us by phone not to text"
              autoFocus
            />
            {add.isError ? <Alert tone="error">{add.error.message}</Alert> : null}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" variant="danger" busy={add.isPending}>
                Mark do not contact
              </Button>
              <Button variant="ghost" onClick={() => setMarking(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div>
            <Button variant="secondary" onClick={() => setMarking(true)}>
              Mark do not contact…
            </Button>
          </div>
        )
      ) : !admin ? (
        <p className="footnote text-ink-muted">Only an admin can lift a do-not-contact.</p>
      ) : null}
    </div>
  );
}

function todayIso(): string {
  return localIsoDate(new Date());
}

function ConsentRecords({
  merchantId,
  people,
}: {
  merchantId: string;
  people: ReadonlyMap<string, string>;
}) {
  const { workspaceId } = useWorkspaceClaims();
  const records = useConsentQuery(workspaceId, merchantId);
  const record = useRecordConsent(workspaceId, merchantId);
  const [adding, setAdding] = useState(false);
  const [source, setSource] = useState<PersonConsentSource>('web_form');
  const [detail, setDetail] = useState('');
  const [scope, setScope] = useState('');
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ scope?: string; date?: string }>({});

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    if (scope.trim() === '') next.scope = 'Say what the merchant agreed to';
    const captured = consentCapturedAt(date, new Date());
    if (!captured.ok) next.date = captured.error;
    setErrors(next);
    if (Object.keys(next).length > 0 || !captured.ok) return;
    record.mutate(
      {
        source,
        sourceDetail: detail,
        scope,
        capturedAt: captured.capturedAt.toISOString(),
        note,
      },
      {
        onSuccess: () => {
          setAdding(false);
          setDetail('');
          setScope('');
          setNote('');
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="consent-records">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="callout font-semibold text-ink">Consent</h3>
        {records.data && records.data.length === 0 ? (
          <span
            className="rounded-pill bg-warning-tint px-2 py-0.5 footnote font-medium text-warning ring-1 ring-warning"
            data-testid="no-consent"
          >
            No consent on file
          </span>
        ) : null}
      </div>
      {records.isPending ? <Spinner label="Loading consent records" /> : null}
      {records.isError ? <Alert tone="error">{records.error.message}</Alert> : null}
      {records.data && records.data.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {records.data.map((c) => (
            <li key={c.id} className="callout" data-testid="consent-record">
              <p className="font-medium text-ink">
                {consentSourceLabel(c.source)}
                {c.sourceDetail ? ` · ${c.sourceDetail}` : ''}
              </p>
              <p className="break-words text-ink-muted">{c.scope}</p>
              {c.note ? <p className="break-words footnote text-ink-muted">{c.note}</p> : null}
              <p className="footnote text-ink-muted">
                {new Date(c.capturedAt).toLocaleDateString()}
                {c.recordedBy
                  ? ` · recorded by ${people.get(c.recordedBy) ?? 'a former member'}`
                  : ' · recorded automatically'}
              </p>
            </li>
          ))}
        </ul>
      ) : null}
      {adding ? (
        <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
          <SelectField
            label="How was consent given?"
            value={source}
            onChange={(e) =>
              setSource(CONSENT_SOURCES.find((s) => s.value === e.target.value)?.value ?? 'other')
            }
            options={CONSENT_SOURCES}
          />
          <TextField
            label="Details (optional)"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            maxLength={300}
            placeholder="Form URL, vendor name, call date…"
          />
          <TextAreaField
            label="What it covers"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            maxLength={500}
            error={errors.scope}
            placeholder="Texts about funding offers from our company"
          />
          <TextField
            label="Date given"
            type="date"
            value={date}
            max={todayIso()}
            onChange={(e) => setDate(e.target.value)}
            error={errors.date}
          />
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
          />
          <p className="footnote text-ink-muted">
            Consent records cannot be edited or deleted. Record a new one to correct it.
          </p>
          {record.isError ? <Alert tone="error">{record.error.message}</Alert> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" busy={record.isPending}>
              Save consent record
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            Add consent record
          </Button>
        </div>
      )}
    </div>
  );
}
